import { lazy, Suspense } from 'react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { BrowserRouter, Route, Routes } from 'react-router'
import { MotionConfig } from 'motion/react'
import { Toaster } from 'sonner'
import { AuthProvider } from '@/lib/auth'
import { useRealtimeSync } from '@/lib/queries'
import { SiteLayout } from '@/components/layout/site-layout'
import { LoadingBlock } from '@/components/ui/feedback'

import HomePage from '@/routes/site/home'
import SquadPage from '@/routes/site/squad'
import PlayerPage from '@/routes/site/player'
import StoryPage from '@/routes/site/story'
import MatchesPage from '@/routes/site/matches'
import MatchPage from '@/routes/site/match'
import ClubPage from '@/routes/site/club'
import NewsPage from '@/routes/site/news'
import ArticlePage from '@/routes/site/article'
import ContactPage from '@/routes/site/contact'
import CmsPage from '@/routes/site/page'
import NotFoundPage from '@/routes/site/not-found'

// The dashboard is a different application wearing the same clothes, and most
// visitors will never open it. Splitting it keeps the public bundle lean.
const CmsApp = lazy(() => import('@/routes/cms/cms-app'))

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Realtime pushes invalidations, so aggressive refetching is unnecessary.
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
})

/** Subscribes once, at the root, for the lifetime of the app. */
function RealtimeBridge() {
  useRealtimeSync()
  return null
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      {/* `reducedMotion="user"` makes every motion component honour the OS
          setting — CSS-level overrides cannot reach JS-driven transforms. */}
      <MotionConfig reducedMotion="user">
        <AuthProvider>
          <BrowserRouter>
            <RealtimeBridge />
            <Routes>
              <Route element={<SiteLayout />}>
                <Route index element={<HomePage />} />
                <Route path="squad" element={<SquadPage />} />
                <Route path="squad/:slug" element={<PlayerPage />} />
                <Route path="story" element={<StoryPage />} />
                <Route path="matches" element={<MatchesPage />} />
                <Route path="matches/:id" element={<MatchPage />} />
                <Route path="club" element={<ClubPage />} />
                <Route path="news" element={<NewsPage />} />
                <Route path="news/:slug" element={<ArticlePage />} />
                <Route path="contact" element={<ContactPage />} />
                {/* Any other single-segment path is looked up as a CMS page, so
                    a page the club creates is live at its slug with no deploy.
                    Static routes above always win the match. */}
                <Route path=":slug" element={<CmsPage />} />
                <Route path="*" element={<NotFoundPage />} />
              </Route>

              <Route
                path="cms/*"
                element={
                  <Suspense
                    fallback={<LoadingBlock className="min-h-dvh" label="Opening dashboard" />}
                  >
                    <CmsApp />
                  </Suspense>
                }
              />
            </Routes>
          </BrowserRouter>

          <Toaster
            theme="dark"
            position="bottom-right"
            toastOptions={{
              style: {
                background: 'var(--color-ink-raised)',
                border: '1px solid var(--color-hairline)',
                color: 'var(--color-bone)',
                borderRadius: '0',
              },
            }}
          />
        </AuthProvider>
      </MotionConfig>
    </QueryClientProvider>
  )
}
